import { Flex } from '@approvalmax/ui/src/components';
import { intl, numberHelpers } from '@approvalmax/utils';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning, WarningIndicator } from 'modules/request';
import { LineItemFooterLine } from 'pages/requestForm/components/lineItemsSection/LineItemFooterLine/LineItemFooterLine';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';

import { useCurrencyPrecision } from '../../../hooks';
import { messages } from './NetSuiteRequestFooterLegacy.messages';
import { Separator, Wrapper } from './NetSuiteRequestFooterLegacy.styles';
import { NetSuiteRequestFooterLegacyProps } from './NetSuiteRequestFooterLegacy.types';

const qa = bemFactory.qa('netsuite-request-form-footer');

/**
 * @deprecated Use redesigned version {@link NetSuiteRequestFooter}
 */
export const NetSuiteRequestFooterLegacy: FC<NetSuiteRequestFooterLegacyProps> = memo((props) => {
    const { netAmount, taxAmount, totalAmount, currency, transactionDiscount, isInvoice = false } = props;

    const request = useSelector(requestSelector);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const currencyPrecision = useCurrencyPrecision(currency);

    const showSubtotalWarning = Boolean(ocrData?.document?.subTotal && netAmount !== ocrData.document.subTotal);
    const showTaxTotalWarning =
        numberHelpers.isNumber(ocrData?.document?.totalTax) && taxAmount !== ocrData.document.totalTax;

    const showTotalWarning = Boolean(ocrData?.document?.total && totalAmount !== ocrData.document.total);

    const currencyName = currency?.text ?? '';

    return (
        <Wrapper>
            <Flex direction='column' alignItems='end' maxWidth={250} wrap={false} spacing='0'>
                <WarningIndicator
                    show={showSubtotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={netAmount}
                            value2={ocrData?.document?.subTotal}
                            label1={messages.differentAmountWarningLabel1}
                            label2={messages.differentAmountWarningLabel2}
                            description={messages.differentAmountWarningDescription}
                        />
                    }
                >
                    <LineItemFooterLine
                        warning={showSubtotalWarning}
                        label={isInvoice ? messages.invoiceNetAmountText : messages.netAmountText}
                        data-qa={qa('net-amount')}
                    >
                        {intl.formatCurrency(netAmount, currencyName, currencyPrecision, false)}
                    </LineItemFooterLine>
                </WarningIndicator>

                {isInvoice && (
                    <LineItemFooterLine
                        label={messages.invoiceTransactionDiscountText}
                        data-qa={qa('transaction-discount')}
                    >
                        {intl.formatCurrency(transactionDiscount, currencyName, currencyPrecision, false)}
                    </LineItemFooterLine>
                )}

                <WarningIndicator
                    show={showTaxTotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={taxAmount}
                            value2={ocrData?.document?.totalTax}
                            label1={messages.differentTaxTotalWarningLabel1}
                            label2={messages.differentTaxTotalWarningLabel2}
                            description={messages.differentTaxTotalWarningDescription}
                        />
                    }
                >
                    <LineItemFooterLine
                        warning={showTaxTotalWarning}
                        label={isInvoice ? messages.invoiceTaxAmountText : messages.taxAmountText}
                        data-qa={qa('tax-amount')}
                    >
                        {intl.formatCurrency(taxAmount, currencyName, currencyPrecision, false)}
                    </LineItemFooterLine>
                </WarningIndicator>

                <Separator />

                <WarningIndicator
                    show={showTotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={totalAmount}
                            value2={ocrData?.document?.total}
                            label1={messages.differentTotalWarningLabel1}
                            label2={messages.differentTotalWarningLabel2}
                            description={messages.differentTaxTotalWarningDescription}
                        />
                    }
                >
                    <LineItemFooterLine
                        warning={showTotalWarning}
                        label={isInvoice ? messages.invoiceTotalAmountText : messages.totalAmountText}
                        data-qa={qa('total-amount')}
                    >
                        {intl.formatCurrency(totalAmount, currencyName, currencyPrecision, false)}
                    </LineItemFooterLine>
                </WarningIndicator>
            </Flex>
        </Wrapper>
    );
});

NetSuiteRequestFooterLegacy.displayName = 'NetSuiteRequestFooterLegacy';
